import { useEffect, useState } from 'react';
import { Alert, Collapse, Form, Input, Modal, Select, Tag, Typography } from 'antd';
import { SLUG_RE, type Space, type SpaceTemplate } from '@lens/shared';
import { ApiError } from '../../shared/api/client.ts';
import { SpaceIconPicker } from '../../shared/components/SpaceIconPicker.tsx';
import { createSpace, listTemplates } from './api.ts';

/**
 * Модальное окно «Новое пространство» (FR-1.3).
 *
 * Контракт с родителем намеренно узкий:
 *  - `open` / `onClose` управляют видимостью, сброс формы — внутри;
 *  - `onCreated(space)` вызывается только после успешного POST,
 *    чтобы родитель мог обновить список без перезагрузки.
 *
 * Валидация:
 *  - `name` — 1..120 символов, trim на сервере;
 *  - `slug` — регулярка `SLUG_RE` из @lens/shared (контракт общий с
 *    backend), чтобы не дублировать; сообщение русское и содержит
 *    пример вместо абстракции «паттерн».
 *  - `description` — опционально, ≤1000; пустая строка превращается в
 *    undefined, чтобы сервер не сохранял пустые строки вместо NULL.
 *
 * Ошибки:
 *  - `slug_conflict` от сервера — поле `slug` подсвечивается
 *    через `form.setFields`, без общего алерта (локальная ошибка).
 *  - Остальные — общий алерт сверху формы.
 */

export interface CreateSpaceModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (space: Space) => void;
}

interface FormValues {
  name: string;
  slug: string;
  description?: string;
  icon?: string | null;
  templateId?: string;
}

const { Text } = Typography;

// Структура шаблона лежит в `template.config` (формируется на бэкенде в
// templates/service.ts): массив типов записей, у каждого — поля и представления.
// Здесь описываем только то, что используем для лёгкого превью.
interface TemplateConfigField {
  name?: string;
}
interface TemplateConfigView {
  name?: string;
}
interface TemplateConfigItemType {
  name?: string;
  icon?: string;
  fields?: TemplateConfigField[];
  views?: TemplateConfigView[];
}
interface TemplateConfig {
  itemTypes?: TemplateConfigItemType[];
}

const SLUG_HINT = 'строчные латинские, цифры и дефис (например, demo-goals)';
const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

function getCommonErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'validation_error') return 'Проверьте введённые данные';
    if (err.code === 'unauthorized') return 'Сессия истекла, войдите заново';
    return err.message || 'Не удалось создать пространство';
  }
  return 'Не удалось создать пространство. Проверьте соединение.';
}

function slugifySpaceName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .split('')
    .map((char) => CYRILLIC_TO_LATIN[char] ?? char)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function CreateSpaceModal({
  open,
  onClose,
  onCreated,
}: CreateSpaceModalProps): JSX.Element {
  const [form] = Form.useForm<FormValues>();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [templates, setTemplates] = useState<SpaceTemplate[]>([]);
  const [slugTouched, setSlugTouched] = useState(false);
  const nameValue = Form.useWatch('name', form);
  const templateIdValue = Form.useWatch('templateId', form);

  const selectedTemplate = templates.find((t) => t.id === templateIdValue);
  const selectedTemplateItemTypes =
    (selectedTemplate?.config as TemplateConfig | undefined)?.itemTypes ?? [];

  // Сбрасываем форму при каждом закрытии, чтобы в следующий раз не
  // увидеть остатки прошлой попытки. Используем `useEffect`, а не
  // обработчик onClose родителя, потому что родитель может закрыть
  // окно «извне» (например, ESC или Cancel).
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setErrorMessage(null);
      setSubmitting(false);
      setSlugTouched(false);
    } else {
      listTemplates()
        .then((res) => setTemplates(res.templates ?? []))
        .catch(() => { /* ignore */ });
    }
  }, [open, form]);

  useEffect(() => {
    if (!open || slugTouched) return;
    form.setFieldValue('slug', slugifySpaceName(nameValue ?? ''));
  }, [form, nameValue, open, slugTouched]);

  const handleOk = async (): Promise<void> => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      // AntD сам подсветил поля, ничего дополнительно делать не надо.
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      const space = await createSpace({
        name: values.name.trim(),
        slug: values.slug.trim().toLowerCase(),
        description: values.description?.trim() || undefined,
        icon: values.icon || undefined,
        templateId: values.templateId || undefined,
      });
      onCreated(space);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'slug_conflict') {
        form.setFields([
          {
            name: 'slug',
            errors: ['Пространство с таким slug уже существует'],
          },
        ]);
      } else {
        setErrorMessage(getCommonErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="Новое пространство"
      open={open}
      onOk={() => {
        void handleOk();
      }}
      onCancel={onClose}
      okText="Создать"
      cancelText="Отмена"
      confirmLoading={submitting}
      destroyOnHidden
      okButtonProps={{ 'data-testid': 'space-modal-submit' }}
      cancelButtonProps={{ 'data-testid': 'space-modal-cancel' }}
    >
      {errorMessage ? (
        <Alert
          type="error"
          message={errorMessage}
          showIcon
          style={{ marginBottom: 16 }}
          data-testid="space-modal-error"
        />
      ) : null}

      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        disabled={submitting}
        autoComplete="off"
      >
        {templates.length > 0 && (
          <Form.Item
            label="Шаблон"
            name="templateId"
            extra="Структура пространства будет создана из шаблона (типы записей, поля, представления)"
          >
            <Select
              allowClear
              placeholder="Без шаблона"
              options={templates.map((t) => ({
                value: t.id,
                label: t.name + (t.description ? ` — ${t.description}` : ''),
              }))}
              data-testid="space-modal-template-select"
            />
          </Form.Item>
        )}
        {selectedTemplate && (
          <div
            data-testid="space-modal-template-preview"
            style={{
              marginTop: -8,
              marginBottom: 16,
              padding: '12px 16px',
              background: 'var(--color-bg-layout)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
            }}
          >
            {selectedTemplate.description && (
              <div style={{ marginBottom: selectedTemplateItemTypes.length > 0 ? 12 : 0 }}>
                <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>
                  {selectedTemplate.description}
                </Text>
              </div>
            )}
            {selectedTemplateItemTypes.length > 0 && (
              <>
                <Text strong style={{ fontSize: 12 }}>
                  Будет создано:
                </Text>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selectedTemplateItemTypes.map((it, idx) => {
                    const fieldNames = (it.fields ?? [])
                      .map((f) => f.name)
                      .filter((n): n is string => Boolean(n));
                    const viewNames = (it.views ?? [])
                      .map((v) => v.name)
                      .filter((n): n is string => Boolean(n));
                    return (
                      <div key={idx} style={{ fontSize: 13 }}>
                        <Text strong>
                          {it.icon ? `${it.icon} ` : ''}
                          {it.name ?? 'Тип записи'}
                        </Text>
                        {fieldNames.length > 0 && (
                          <div style={{ marginTop: 2 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Поля:{' '}
                            </Text>
                            {fieldNames.slice(0, 6).map((n) => (
                              <Tag key={n} style={{ marginInlineEnd: 4, marginBottom: 4 }}>
                                {n}
                              </Tag>
                            ))}
                            {fieldNames.length > 6 && (
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                +{fieldNames.length - 6}
                              </Text>
                            )}
                          </div>
                        )}
                        {viewNames.length > 0 && (
                          <div style={{ marginTop: 2 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Представления: {viewNames.join(', ')}
                            </Text>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
        <Form.Item label="Иконка" name="icon" valuePropName="value">
          <SpaceIconPicker name={nameValue ?? ''} />
        </Form.Item>
        <Form.Item
          label="Название"
          name="name"
          rules={[
            { required: true, message: 'Укажите название' },
            { max: 120, message: 'Не более 120 символов' },
          ]}
        >
          <Input autoFocus data-testid="space-modal-name-input" />
        </Form.Item>
        <Form.Item
          label="Описание"
          name="description"
          rules={[{ max: 1000, message: 'Не более 1000 символов' }]}
        >
          <Input.TextArea
            rows={3}
            data-testid="space-modal-description-input"
          />
        </Form.Item>
        <Collapse
          ghost
          items={[
            {
              key: 'adv',
              label: 'Дополнительно',
              forceRender: true,
              children: (
                <Form.Item
                  label="Slug"
                  name="slug"
                  extra={SLUG_HINT}
                  rules={[
                    { required: true, message: 'Укажите slug' },
                    {
                      pattern: SLUG_RE,
                      message: `Допустимы только ${SLUG_HINT}`,
                    },
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    data-testid="space-modal-slug-input"
                    onChange={() => setSlugTouched(true)}
                  />
                </Form.Item>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
}
